/**
 * The Enriched data tab's look (Circuitry's cty-ux-designs tokens: text #0B1B3D, muted #5C6B85,
 * borders #E5E7EB / #CBD5E1, primary #0B1B3D, accent #D4017E), as Tailwind classes and a few small
 * pieces: buttons, badges, chips, the design's icons, and keyboard handling for its dialogs.
 *
 * Every colour has a `dark:` partner for DOC's dark mode (`.dark` on <html>, see useDarkMode), on
 * DOC's dark palette: surface #0d1117, raised #161b22, track #21262d, borders #30363d (fields
 * #454c56), text #e6edf3, muted #8b949e, links #58a6ff; the navy primary turns light, and tinted
 * badges become a translucent tint with light text.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import type { Tone } from './format';

export const MONO = "font-['JetBrains_Mono',ui-monospace,SFMono-Regular,monospace]";

// Colour pairs, light then dark.
export const TEXT = 'text-[#0B1B3D] dark:text-[#e6edf3]';
export const MUTED = 'text-[#5C6B85] dark:text-[#8b949e]';
/** Secondary text: labels of fields, chips off. */
export const SOFT = 'text-[#434A59] dark:text-[#c9d1d9]';
export const SURFACE = 'bg-white dark:bg-[#0d1117]';
/** A panel or header a step above the surface. */
export const RAISED = 'bg-[#F7F8FA] dark:bg-[#161b22]';
export const BORDER = 'border-[#E5E7EB] dark:border-[#30363d]';
/** A progress bar's empty track. */
export const TRACK = 'bg-[#F1F2F5] dark:bg-[#21262d]';
/** A progress bar's (navy) fill. */
export const FILL = 'bg-[#0B1B3D] dark:bg-[#e6edf3]';

/** The small uppercase label over a value or a table column. */
export const LABEL = `text-[11px] font-bold uppercase tracking-[.05em] ${MUTED}`;
export const CARD = `rounded-xl border ${BORDER} ${SURFACE}`;
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB] dark:focus-visible:outline-[#58a6ff]';
export const FIELD = `h-10 box-border rounded-lg border border-[#CBD5E1] bg-white px-3 text-sm text-[#0B1B3D] dark:border-[#454c56] dark:bg-[#0d1117] dark:text-[#e6edf3] ${FOCUS}`;
export const LINK = `cursor-pointer border-0 bg-transparent p-0 text-left text-[13px] font-semibold text-[#2563EB] hover:text-[#1D4ED8] dark:text-[#58a6ff] dark:hover:text-[#79c0ff] ${FOCUS}`;

/** The design's button: secondary by default, `primary` dark, `small` 32 px, `danger` red text. */
export function buttonClass({ primary = false, small = false, danger = false }: { primary?: boolean; small?: boolean; danger?: boolean } = {}): string {
  const size = small ? 'h-8 px-3 text-[13px]' : 'h-10 px-4 text-sm';
  let colour = 'border-[#CBD5E1] bg-white text-[#0B1B3D] hover:bg-[#F7F8FA] dark:border-[#30363d] dark:bg-[#161b22] dark:text-[#e6edf3] dark:hover:bg-[#21262d]';
  // Dark: the navy primary becomes a light button.
  if (primary) colour = 'border-[#0B1B3D] bg-[#0B1B3D] text-white hover:bg-[#1B2B4D] dark:border-[#e6edf3] dark:bg-[#e6edf3] dark:text-[#0d1117] dark:hover:border-[#c9d1d9] dark:hover:bg-[#c9d1d9]';
  if (danger) colour = 'border-[#FCA5A5] bg-white text-[#B91C1C] hover:bg-[#FEF2F2] dark:border-[#F87171]/50 dark:bg-[#161b22] dark:text-[#F87171] dark:hover:bg-[#EF4444]/10';
  return `inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border font-semibold ${size} ${colour} disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS}`;
}

/** A pill: on, it is dark. */
export function chipClass(on: boolean): string {
  const colour = on
    ? 'border-[#0B1B3D] bg-[#0B1B3D] text-white dark:border-[#e6edf3] dark:bg-[#e6edf3] dark:text-[#0d1117]'
    : 'border-[#E5E7EB] bg-white text-[#434A59] hover:bg-[#F7F8FA] dark:border-[#30363d] dark:bg-[#0d1117] dark:text-[#c9d1d9] dark:hover:bg-[#161b22]';
  return `inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[13px] font-medium ${colour} ${FOCUS}`;
}

/** A tone's text colour alone (counts, notes, done steps). */
export const TONE_TEXT: Record<Tone, string> = {
  ok: 'text-[#047857] dark:text-[#34D399]',
  warn: 'text-[#92400E] dark:text-[#FBBF24]',
  bad: 'text-[#B91C1C] dark:text-[#F87171]',
  info: 'text-[#1D4ED8] dark:text-[#60A5FA]',
  neutral: 'text-[#434A59] dark:text-[#c9d1d9]',
};

/** A badge's or icon tile's tint and text; dark, a translucent tint with light text. */
export const TONES: Record<Tone, string> = {
  ok: 'bg-[#D1FAE5] text-[#047857] dark:bg-[#10B981]/15 dark:text-[#34D399]',
  warn: 'bg-[#FEF3C7] text-[#92400E] dark:bg-[#F59E0B]/15 dark:text-[#FBBF24]',
  bad: 'bg-[#FEE2E2] text-[#B91C1C] dark:bg-[#EF4444]/15 dark:text-[#F87171]',
  info: 'bg-[#DBEAFE] text-[#1D4ED8] dark:bg-[#3B82F6]/15 dark:text-[#60A5FA]',
  neutral: 'bg-[#F1F2F5] text-[#434A59] dark:bg-[#8b949e]/15 dark:text-[#c9d1d9]',
};

export function Badge({ tone, children }: { readonly tone: Tone; readonly children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[3px] text-xs font-semibold ${TONES[tone]}`}>
      {children}
    </span>
  );
}

/** A small turning ring, for "checking", in the text colour around it. */
export function Ring({ size = 8 }: { readonly size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="box-border inline-block shrink-0 animate-spin rounded-full border-2 border-solid border-current !border-t-transparent"
      style={{ width: size, height: size }}
    />
  );
}

export function SrOnly({ children, ...props }: { readonly children: ReactNode; readonly role?: string; readonly 'aria-live'?: 'polite' }) {
  return <span className="sr-only" {...props}>{children}</span>;
}

// -- Icons (the mockups' strokes, 24-unit view box) ---------------------------------------------

const PATHS = {
  file: ['M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z', 'M14 3v6h6'],
  sheet: ['M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z', 'M14 3v6h6', 'M8 13l4 4M12 13l-4 4'],
  upload: ['M12 16V4', 'M7 9l5-5 5 5', 'M4 20h16'],
  download: ['M12 4v12', 'M7 11l5 5 5-5', 'M4 20h16'],
  search: ['M20 20l-4-4'],
  chevronRight: ['M9 6l6 6-6 6'],
  chevronDown: ['M6 9l6 6 6-6'],
  close: ['M6 6l12 12M18 6L6 18'],
  minus: ['M5 12h14'],
  copy: ['M5 15V5a2 2 0 0 1 2-2h10'],
  refresh: ['M20 11a8 8 0 1 0-2.3 5.7', 'M20 4v7h-7'],
  check: ['M5 12l5 5 9-9'],
  shield: ['M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z', 'M9 12l2 2 4-4'],
  clock: ['M12 7v5l3 2'],
  alert: ['M12 8v5', 'M12 16.5v.5'],
  info: ['M12 8v.5', 'M12 11v5'],
  monitor: ['M8 20h8'],
} as const;

export type IconName = keyof typeof PATHS;

/** An icon in the text colour around it, or `className`'s (e.g. TONE_TEXT.ok). */
export function Icon({ name, size = 16, className = '', strokeWidth = 2 }: {
  readonly name: IconName;
  readonly size?: number;
  readonly className?: string;
  readonly strokeWidth?: number;
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} aria-hidden="true" className={`shrink-0 ${className}`}>
      {name === 'search' && <circle cx="11" cy="11" r="7" />}
      {(name === 'clock' || name === 'alert' || name === 'info') && <circle cx="12" cy="12" r="9" />}
      {name === 'copy' && <rect x="9" y="9" width="11" height="11" rx="2" />}
      {name === 'monitor' && <rect x="3" y="4" width="18" height="12" rx="2" />}
      {PATHS[name].map((d) => <path key={d} d={d} />)}
    </svg>
  );
}

/** The upload drop zone's icon: grey tint, navy icon. */
const PLAIN = 'bg-[#F1F2F5] text-[#0B1B3D] dark:bg-[#21262d] dark:text-[#e6edf3]';

/** A tile's or panel's square icon on a tinted background, in a badge tone. */
export function IconTile({ name, tone, size = 28, iconSize = 15 }: {
  readonly name: IconName;
  readonly tone: Tone | 'plain';
  readonly size?: number;
  readonly iconSize?: number;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center ${tone === 'plain' ? PLAIN : TONES[tone]}`}
      style={{ width: size, height: size, borderRadius: size > 30 ? 10 : 7 }}
    >
      <Icon name={name} size={iconSize} />
    </span>
  );
}

// -- Dialogs ------------------------------------------------------------------------------------

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

function focusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((element) => !element.closest('[hidden]') && !element.closest('.hidden'));
}

/**
 * A modal dialog's keyboard: focus moves into it when it opens (the dialog itself, so its name is
 * read) and back where it was when it closes (or to `fallback`); Tab stays inside; Escape calls
 * `onEscape`. Spread `onKeyDown` on the dialog, which needs tabIndex -1.
 */
export function useDialog(ref: RefObject<HTMLElement>, onEscape: () => void, open = true, fallback?: RefObject<HTMLElement>) {
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : null;
    ref.current?.focus();
    return () => {
      // Back where focus was; when that is gone (the button that opened it was replaced), the fallback.
      if (previous?.isConnected) previous.focus();
      else fallbackRef.current?.current?.focus();
    };
  }, [open, ref]);
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      escapeRef.current();
      return;
    }
    const container = ref.current;
    if (event.key !== 'Tab' || !container) return;
    const items = focusables(container);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };
  return { onKeyDown };
}

/** Seconds since `busy` turned on, ticking while it stays on. */
export function useElapsed(busy: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!busy) return undefined;
    setSeconds(0);
    const started = Date.now();
    const timer = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);
  return seconds;
}

/** The dimmed page behind a dialog; a click on it calls `onClick`. */
export function Backdrop({ onClick }: { readonly onClick?: () => void }) {
  return <div aria-hidden="true" className="fixed inset-0 z-[60] bg-[rgba(11,27,61,0.32)] dark:bg-[rgba(1,4,9,0.72)]" onClick={onClick} />;
}
